import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetDiscountEditor } from 'redux/modules/DiscountEditor/discountEditor';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { categoriesSelectors } from 'redux/modules/Categories';
import { discountEditorSelectors } from 'redux/modules/DiscountEditor';
import { alertSelectors } from 'redux/modules/Alert';
import {
  closeCategoriesPopUp,
  showCategoriesPopUp,
} from 'redux/modules/Alert/alert';
import { DiscountEditorExitModal } from 'view/pages/DiscountEditor/components/DiscountEditorExitModal';
import {
  allCategories,
  favoriteSidebar,
  archive,
  message,
  statistics,
  unlock,
  users,
} from 'static/icons/resizable';
import { useCheckRoles } from 'hooks';
import { UserRole } from 'interfaces';
import { ProvideAccessModal } from 'view/organisms/ProfileMenu/components';
import { getCategory } from 'redux/modules/Categories/categories';
import { CategoryDropdown } from './components/CategoryDropdown';
import { CategoriesItem } from './components/CategoriesItem';
import * as S from './MenuCategories.styles';

type Props = {
  paddingNone?: boolean;
};

// TODO: Refactor to atom, it shouldn't work with data
export const MenuCategories = ({ paddingNone }: Props) => {
  const { t } = useTranslation();

  const [showAccessModal, setShowAccessModal] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const accessModalRef = useRef<HTMLDivElement>(null);

  const { pathname } = useLocation();

  const isOnDiscountPage = pathname.startsWith(ROUTES.discountRoot);

  const routeMatch = {
    isOnMain: pathname === ROUTES.main && !showAccessModal,
    isOnFavorites: pathname === ROUTES.favorites && !showAccessModal,
    isOnMyRequests: pathname === ROUTES.requestDiscountAll,
    isOnRolesManagement:
      pathname === ROUTES.rolesManagement && !showAccessModal,
    isOnStatistics: pathname === ROUTES.statistics && !showAccessModal,
    isOnNotifications:
      pathname === ROUTES.profileNotifications && !showAccessModal,
    isOnArchives: pathname === ROUTES.archives && !showAccessModal,
  };

  const categories = useAppSelector(categoriesSelectors.getCategories);

  const isCategoriesPopUpOpen = useAppSelector(
    alertSelectors.isCategoriesPopUpOpen,
  );

  const discountCountry = useAppSelector(
    discountEditorSelectors.getDiscountCountry,
  );
  const preSavedRedirectPath = useRef<string | null>(null);

  useEffect(() => {
    if (!categories) dispatch(getCategory());
  }, []);

  const isDiscountEditorInProcess = () =>
    pathname.includes(ROUTES.discountEditor) && discountCountry?.countryName;

  const redirectHandler = (path: string) => {
    if (isDiscountEditorInProcess()) {
      dispatch(showCategoriesPopUp());

      preSavedRedirectPath.current = path;
    } else {
      navigate(path);
    }
  };

  const closeExitModal = () => {
    dispatch(closeCategoriesPopUp());
  };

  const onConfirmHandler = () => {
    navigate(preSavedRedirectPath.current ?? ROUTES.main);

    dispatch(resetDiscountEditor());
    dispatch(closeCategoriesPopUp());
  };

  const onProvideAccessClick = () => {
    setShowAccessModal(true);
  };

  const showMyFavoritesButton = useCheckRoles(
    UserRole.User,
    UserRole.Mod,
    UserRole.Super,
    UserRole.Admin,
    UserRole.Alumni,
    UserRole.Guest,
  );

  const showMyRequestsButton = useCheckRoles(
    UserRole.User,
    UserRole.Mod,
    UserRole.Admin,
  );
  const showRolesManagementButton = useCheckRoles(UserRole.Super);
  const showProvideAccessButton = useCheckRoles(UserRole.Super);
  const showStatisticsButton = useCheckRoles(
    UserRole.Mod,
    UserRole.Super,
    UserRole.Admin,
  );
  const showArchiveButton = useCheckRoles(UserRole.Mod, UserRole.Super);

  return (
    <>
      <S.MenuCategoriesStyles
        paddingNone={paddingNone}
        $open={isCategoriesOpen || isOnDiscountPage}
        className='safe-to-click'
      >
        <CategoryDropdown
          icon={allCategories}
          title={t('menuCategoriesSidebar.main')}
          isActive={routeMatch.isOnMain}
          onClick={() => redirectHandler(ROUTES.main)}
          data-testid='category-main'
          data={categories}
          className='safe-to-click'
          onExpandedChange={setIsCategoriesOpen}
          forceExpanded={isOnDiscountPage}
        />
        {showMyFavoritesButton && (
          <CategoriesItem
            key={ROUTES.favorites}
            text={t('favoritesPage.title')}
            icon={favoriteSidebar}
            onClick={() => redirectHandler(ROUTES.favorites)}
            active={routeMatch.isOnFavorites}
            data-testid='category-favorites'
          />
        )}
        {showMyRequestsButton && (
          <CategoriesItem
            key={ROUTES.requestDiscountAll}
            text={t('other.myRequests')}
            icon={message}
            onClick={() => redirectHandler(ROUTES.requestDiscountAll)}
            active={routeMatch.isOnMyRequests}
          />
        )}
        {showStatisticsButton && (
          <CategoriesItem
            key={ROUTES.statistics}
            text={t('statistics.title')}
            icon={statistics}
            onClick={() => redirectHandler(ROUTES.statistics)}
            active={routeMatch.isOnStatistics}
          />
        )}
        {showRolesManagementButton && (
          <CategoriesItem
            key={ROUTES.rolesManagement}
            text={t('rolesManagementPage.title')}
            icon={users}
            onClick={() => redirectHandler(ROUTES.rolesManagement)}
            active={routeMatch.isOnRolesManagement}
          />
        )}
        {showProvideAccessButton && (
          <CategoriesItem
            key='discountAccessModal'
            text={t('profile.access')}
            icon={unlock}
            onClick={() => onProvideAccessClick()}
            active={showAccessModal}
            data-testid='button-discount-access'
          />
        )}
        {showArchiveButton && (
          <CategoriesItem
            key={ROUTES.archives}
            text={t('archives.title')}
            icon={archive}
            onClick={() => redirectHandler(ROUTES.archives)}
            active={routeMatch.isOnArchives}
          />
        )}
        {/* 
        {categories?.map(({ id, title, iconLink }) => {
          const slug = getSlug(title);
          return (
            <CategoriesItem
              key={id}
              text={translateCategory(title, language)}
              icon={iconLink}
              onClick={() => redirectHandler(`${ROUTES.categoryRoot}/${id}`)}
              active={pathname.includes(`${ROUTES.categoryRoot}/${id}`)}
              data-testid={`category-${slug}`}
            />
          );
        })} */}
      </S.MenuCategoriesStyles>

      {isCategoriesPopUpOpen && (
        <DiscountEditorExitModal
          isShown={isCategoriesPopUpOpen}
          closeHandler={closeExitModal}
          confirmHandler={onConfirmHandler}
        />
      )}

      {showAccessModal && (
        <ProvideAccessModal
          modalRef={accessModalRef}
          closeHandler={() => setShowAccessModal(false)}
        />
      )}
    </>
  );
};
